import {
    ContouringCustomizationFile,
    CustomizationItem,
    UserCustomization,
} from '~/backend/types';

const DEFAULT_CUSTOMIZATION_NAME = 'default';

function findModelByName(
    models: UserCustomization<ContouringCustomizationFile>[],
    modelName?: string
): UserCustomization<ContouringCustomizationFile> | undefined {
    return models.find((model) => model.model_name === modelName);
}

function findCustomizationByName(
    model: UserCustomization<ContouringCustomizationFile>,
    customizationName: string
): CustomizationItem<ContouringCustomizationFile> | undefined {
    return model.customizations.find(
        (customization) =>
            customization.customization_name === customizationName
    );
}

function extractRoiNamesFromCustomization(
    customization: CustomizationItem<ContouringCustomizationFile>
): { name: string; included: boolean }[] {
    return customization.files.flatMap((file) =>
        file.rois.map((roi) => ({ name: roi.name, included: roi.included }))
    );
}

/**
 * Returns an array of unique ROI names from the target model and its customizations.
 *
 * @param models - An array of UserCustomization objects
 * @param modelName - The name of the target model
 * @param customizationName - The name of the customization (optional)
 * @returns An array of unique ROI names
 */
export function transformModelRoiNames(
    models: UserCustomization<ContouringCustomizationFile>[],
    modelName?: string,
    customizationName?: string
): { roiNames: string[]; includedRoiNames: string[] } {
    const targetModel = findModelByName(models, modelName);
    if (!targetModel) return { roiNames: [], includedRoiNames: [] };

    const targetCustomization = customizationName
        ? findCustomizationByName(targetModel, customizationName)
        : findCustomizationByName(targetModel, DEFAULT_CUSTOMIZATION_NAME);

    if (!targetCustomization) return { roiNames: [], includedRoiNames: [] };

    const rois = extractRoiNamesFromCustomization(targetCustomization);
    const roiNames = rois.map((roi) => roi.name);
    const includedRoiNames = rois
        .filter((roi) => roi.included)
        .map((roi) => roi.name);

    return { roiNames, includedRoiNames };
}
